import { useEffect, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LIST_VIEW_DEFAULT_SORT,
  listViewStateSchema,
  listViewsResponseSchema,
  listViewResolutionSchema,
  type ListViewState,
  type ListViewList,
} from '@erp/shared';
import { apiFetch } from '@/lib/api-client';

export function useListView(list: ListViewList, initial: ListViewState) {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const client = useQueryClient();
  const defaults = useRef(initial);
  defaults.current = initial;
  const views = useQuery({
    queryKey: ['list-views', list],
    queryFn: () => apiFetch(`/api/v1/list-views?list=${list}`, listViewsResponseSchema),
  });
  const id = params.get('_view');
  const encoded = params.get('_state');
  const encodedVersion = Number(params.get('_version'));
  const linked = useQuery({
    queryKey: ['list-view', id, list],
    queryFn: () =>
      apiFetch(`/api/v1/list-views/${encodeURIComponent(id ?? '')}`, listViewResolutionSchema),
    enabled: id !== null,
  });
  let state = linked.data?.view?.list === list ? linked.data.state : defaults.current;
  if (id === null && encoded === null) {
    const storageKey =
      list === 'products'
        ? 'erp-products-columns'
        : ['customers', 'prospects', 'suppliers', 'partners'].includes(list)
          ? `erp-partners-columns-${list === 'partners' ? 'all' : list.slice(0, -1)}`
          : null;
    if (storageKey) {
      try {
        const stored: unknown = JSON.parse(window.localStorage.getItem(storageKey) ?? '{}');
        if (typeof stored === 'object' && stored !== null && !Array.isArray(stored)) {
          const visibility = stored as Record<string, unknown>;
          state = {
            ...state,
            columns: state.columns.map((column) =>
              typeof visibility[column.id] === 'boolean'
                ? { ...column, visible: visibility[column.id] === true }
                : column,
            ),
          };
        }
      } catch {
        // Unreadable legacy preferences leave the factory columns intact.
        state = defaults.current;
      }
    }
  }
  let malformed = false;
  if (encoded !== null) {
    try {
      state = listViewStateSchema.parse(JSON.parse(encoded));
    } catch {
      malformed = true;
    }
  }
  const storedColumns = new Set(state.columns.map((column) => column.id));
  state = {
    ...state,
    columns: [
      ...state.columns,
      ...initial.columns
        .filter((column) => !storedColumns.has(column.id))
        .map((column) => ({ ...column, visible: false })),
    ],
  };
  const hasExplicitParams = [...params.keys()].some((key) => key !== 'tab');
  const defaultPending =
    !hasExplicitParams &&
    (views.isPending || (views.data?.defaultId !== undefined && views.data.defaultId !== null));
  useEffect(() => {
    if (!hasExplicitParams && views.data?.defaultId) {
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          next.set('_view', views.data.defaultId ?? '');
          return next;
        },
        { replace: true },
      );
    }
  }, [hasExplicitParams, views.data?.defaultId, setParams]);
  const linkedReady =
    id === null ||
    (linked.isSuccess &&
      !linked.isFetching &&
      linked.data.view?.list === list &&
      linked.data.issues.length === 0);
  const resolved = useQuery({
    queryKey: ['list-view-state', list, state],
    queryFn: () =>
      apiFetch('/api/v1/list-views/resolve', listViewResolutionSchema, {
        method: 'POST',
        body: { list, state },
      }),
    refetchInterval: 60_000,
    enabled: !malformed && !defaultPending && (linkedReady || encoded !== null),
  });
  const issues =
    encoded === null && linked.data?.issues.length
      ? linked.data.issues
      : (resolved.data?.issues ?? []);
  const error = malformed
    ? 'listViews.invalid'
    : linked.isError
      ? linked.error.message
      : views.isError
        ? views.error.message
        : resolved.isError
          ? resolved.error.message
          : id !== null && linked.isSuccess && linked.data.view?.list !== list
            ? 'listViews.wrongList'
            : null;
  const ready =
    !error &&
    !defaultPending &&
    (linkedReady || encoded !== null) &&
    resolved.isSuccess &&
    issues.length === 0;
  // Local edits retain the version they started from, including across reloads.
  const writeVersion =
    encoded === null
      ? linked.data?.view?.version
      : Number.isSafeInteger(encodedVersion) && encodedVersion > 0
        ? encodedVersion
        : undefined;
  const commit = (nextState: ListViewState, replace = false): void => {
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (id !== null && !next.has('_version') && writeVersion !== undefined)
          next.set('_version', String(writeVersion));
        next.set('_state', JSON.stringify(nextState));
        next.set('page', '1');
        return next;
      },
      { replace },
    );
  };
  const setFilter = (key: string, value: string | undefined): void => {
    const filters = { ...state.filters };
    if (value === undefined || value === '') delete filters[key];
    else filters[key] = value;
    commit({ ...state, filters });
  };
  const open = (viewId: string | null): void => {
    if (viewId) void client.invalidateQueries({ queryKey: ['list-view', viewId, list] });
    setParams((previous) => {
      // The product's tab is context, not a saved filter.
      const next = new URLSearchParams();
      if (previous.has('tab')) next.set('tab', previous.get('tab') ?? '');
      if (viewId) next.set('_view', viewId);
      else
        next.set(
          '_state',
          JSON.stringify({
            ...defaults.current,
            sort: LIST_VIEW_DEFAULT_SORT[list],
            filters: {},
            search: undefined,
            period: undefined,
            fieldRefs: {},
          }),
        );
      return next;
    });
  };
  const setPage = (value: number | ((previous: number) => number)): void => {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set('page', String(typeof value === 'function' ? value(page) : value));
      return next;
    });
  };
  const page = Math.max(1, Number(params.get('page')) || 1);
  return {
    list,
    state,
    effectiveFilters: resolved.data?.effectiveFilters ?? state.filters,
    views,
    view: linked.data?.view ?? null,
    issues,
    error,
    ready,
    page,
    setPage,
    commit,
    setFilter,
    open,
    savedState: linked.data?.state,
    writeVersion,
    dirty:
      encoded !== null &&
      JSON.stringify(state) !== JSON.stringify(linked.data?.state ?? defaults.current),
    retry: () => {
      void resolved.refetch();
      if (id !== null) void linked.refetch();
      void views.refetch();
    },
    refresh: async () => {
      await client.invalidateQueries({ queryKey: ['list-views', list] });
      await client.invalidateQueries({ queryKey: ['list-view', id, list] });
    },
    contextKey: `${location.pathname}:${list}`,
  };
}
export type ListViewController = ReturnType<typeof useListView>;
